/*
 * Evidencia: lo que se trajo de cada sesión — grabación Plaud, resumen, fotos,
 * video, notas — agrupado por sesión, como en el prototipo del cliente. Debajo de
 * cada sesión, el pipeline: grabación → transcripción → hallazgos propuestos →
 * verificados → en el grafo, con el atajo a la cola cuando hay pendientes.
 *
 * Los hallazgos ya verificados se listan al final, por tipo y con su fuente a la
 * vista: es la salida de este pipeline y el sitio donde se comprueba de dónde
 * viene cada afirmación (principio 5).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useCandidatos, usePersonas, useSesiones, useVerificados } from "../../api/hooks";
import { SESIONES, TIPOS_ENTRADA, TIPOS_HALLAZGO, type CasoResumen, type CuentaDetalle, type Hallazgo, type Sesion, type TipoHallazgo } from "../../api/tipos";
import { useFormularios } from "../../shell/Formularios";
import { Abierto, CabeceraSeccion, Esqueleto, Pastilla, fmtFecha, fmtMs } from "../../ui/primitivas";

const ORDEN: TipoHallazgo[] = ["dolor", "metrica", "oportunidad", "ansiedad", "quote", "observacion"];

export function Evidencia({ c, caso }: { c: CuentaDetalle; caso: CasoResumen | null }) {
  const { abrir } = useFormularios();
  const id = caso?.id ?? null;
  const { data: sesiones, isLoading } = useSesiones(id);
  const { data: verificados } = useVerificados(id);
  const { data: candidatos } = useCandidatos(id);
  const { data: personas } = usePersonas(id);

  if (!caso) {
    return (
      <div style={{ maxWidth: 900 }}>
        <CabeceraSeccion titulo="Evidencia" />
        <Abierto>Nada subido aún. Grabaciones, resúmenes Plaud, fotos, video y notas; cada entrada se asigna a una de las cuatro sesiones. Para empezar, <a href="#" onClick={(e) => { e.preventDefault(); abrir({ tipo: "descubrimiento", cuentaId: c.id, empresa: c.empresa }); }} style={{ color: "var(--open-text)", textDecoration: "underline" }}>inicia el descubrimiento</a>.</Abierto>
      </div>
    );
  }
  if (isLoading || !sesiones) return <div className="tarjeta"><Esqueleto lineas={4} /></div>;

  const total = sesiones.reduce((n, s) => n + s.entradas.length, 0);
  const abrirEntrada = (sesionId?: string) => abrir({ tipo: "entrada", casoId: caso.id, cuentaId: c.id, sesiones, sesionId, personas: personas ?? [] });
  const orden = [...sesiones].sort((a, b) => (SESIONES.findIndex((x) => x.id === a.tipo) - SESIONES.findIndex((x) => x.id === b.tipo)) || a.fecha.localeCompare(b.fecha));

  return (
    <div style={{ maxWidth: 900 }}>
      <CabeceraSeccion titulo="Evidencia" n={total} derecha={<button className="btn btn-primario" onClick={() => abrirEntrada()}>+ Agregar evidencia</button>} />
      {sesiones.length === 0 && (
        <Abierto style={{ marginBottom: 14 }}>Nada subido aún. La evidencia cuelga de una sesión; <Link to={`/cuentas/${c.id}/descubrimiento`} style={{ color: "var(--open-text)", textDecoration: "underline" }}>registra la primera</Link> y sube ahí la grabación, las fotos y las notas.</Abierto>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {orden.map((s) => {
          const def = SESIONES.find((x) => x.id === s.tipo);
          const propuestos = (candidatos ?? []).filter((h) => h.sesion_id === s.id).length;
          const enGrafo = (verificados ?? []).filter((h) => h.sesion_id === s.id).length;
          return (
            <section key={s.id} className="tarjeta">
              <CabeceraSeccion titulo={`Sesión ${def?.num ?? "?"} · ${def?.nombre ?? s.tipo}`} n={s.entradas.length} derecha={<span className="mono" style={{ color: "var(--faint)" }}>{fmtFecha(s.fecha, true)}</span>} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px,1fr))", gap: 8 }}>
                {s.entradas.map((e) => (
                  <div key={e.id} title={e.referencia ?? undefined} style={{ background: "var(--sunken)", borderRadius: "var(--r-control)", padding: "10px 12px", display: "flex", gap: 10, alignItems: "flex-start", minWidth: 0 }}>
                    <span style={{ width: 28, height: 28, borderRadius: 6, background: "var(--navy)", color: "#fff", display: "grid", placeItems: "center", fontSize: 12, flexShrink: 0 }}>{TIPOS_ENTRADA[e.tipo].glifo}</span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.etiqueta}</span>
                      <span className="mono" style={{ display: "block", color: "var(--faint)", fontSize: 11 }}>{e.detalle || TIPOS_ENTRADA[e.tipo].label}</span>
                    </span>
                  </div>
                ))}
                <button onClick={() => abrirEntrada(s.id)} className="abierto" style={{ padding: "10px 12px", fontSize: 12.5, lineHeight: 1.4, minHeight: 52, borderRadius: "var(--r-control)" }}>
                  <span style={{ fontWeight: 500 }}>+ Agregar entrada</span><br /><span style={{ opacity: .8 }}>grabación · resumen Plaud · fotos · video · nota</span>
                </button>
              </div>
              {(s.entradas.length > 0 || propuestos > 0 || enGrafo > 0) && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                  <Pipeline s={s} propuestos={propuestos} enGrafo={enGrafo} casoId={caso.id} />
                </div>
              )}
            </section>
          );
        })}
      </div>
      <Grafo verificados={verificados ?? []} sesiones={sesiones} personas={new Map((personas ?? []).map((p) => [p.id, p.nombre]))} />
    </div>
  );
}

function Pipeline({ s, propuestos, enGrafo, casoId }: { s: Sesion; propuestos: number; enGrafo: number; casoId: string }) {
  const audio = s.entradas.some((e) => e.tipo === "audio") || s.grabada;
  const total = propuestos + enGrafo;
  const pasos: { l: string; ok: boolean; n?: number; warn?: boolean }[] = [
    { l: "Grabación", ok: audio },
    { l: "Transcripción", ok: s.entradas.some((e) => e.tipo === "audio" && /transcri/i.test(e.detalle)) },
    { l: "Hallazgos propuestos", ok: total > 0, n: total > 0 ? total : undefined },
    { l: "Verificados", ok: total > 0 && propuestos === 0, n: total > 0 ? enGrafo : undefined, warn: propuestos > 0 },
    { l: "En el grafo", ok: enGrafo > 0 },
  ];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 12.5 }}>
      {pasos.map((p, i) => (
        <span key={p.l} style={{ display: "contents" }}>
          {i > 0 && <span style={{ color: "var(--border-strong)" }}>→</span>}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap",
            background: p.warn ? "var(--open-bg)" : p.ok ? "var(--verified-bg)" : "var(--sunken)",
            color: p.warn ? "var(--open-text)" : p.ok ? "var(--verified)" : "var(--faint)",
            border: p.warn ? "1px dashed var(--open-border)" : "1px solid transparent" }}>
            {p.l}{p.n !== undefined && <span className="mono" style={{ fontSize: 11 }}>{p.n}</span>}
          </span>
        </span>
      ))}
      {propuestos > 0 && <Link to={`/verificacion/${casoId}`} style={{ marginLeft: "auto", color: "var(--sky)", fontWeight: 500, fontSize: 12.5 }}>Verificar {propuestos} →</Link>}
    </div>
  );
}

function Grafo({ verificados, sesiones, personas }: { verificados: Hallazgo[]; sesiones: Sesion[]; personas: Map<string, string> }) {
  const [filtro, setFiltro] = useState<TipoHallazgo | "todos">("todos");
  const sesionNombre = useMemo(() => new Map(sesiones.map((s) => [s.id, SESIONES.find((x) => x.id === s.tipo)?.nombre ?? s.tipo])), [sesiones]);
  const conteo = new Map<TipoHallazgo, number>();
  for (const h of verificados) conteo.set(h.tipo, (conteo.get(h.tipo) ?? 0) + 1);
  const grupos = ORDEN.filter((t) => conteo.has(t) && (filtro === "todos" || filtro === t)).map((t) => [t, verificados.filter((h) => h.tipo === t)] as const);
  if (verificados.length === 0) return null;
  return (
    <section style={{ marginTop: 28 }}>
      <CabeceraSeccion titulo="En el grafo" n={verificados.length} derecha={<span style={{ fontSize: 12, color: "var(--faint)" }}>hallazgos verificados por una persona, con su fuente</span>} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        <button className={"chip" + (filtro === "todos" ? " activo" : "")} onClick={() => setFiltro("todos")}>Todos</button>
        {ORDEN.filter((t) => conteo.has(t)).map((t) => <button key={t} className={"chip" + (filtro === t ? " activo" : "")} onClick={() => setFiltro(filtro === t ? "todos" : t)}>{TIPOS_HALLAZGO[t]} <span className="mono" style={{ fontSize: 11, opacity: .7 }}>{conteo.get(t)}</span></button>)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {grupos.map(([tipo, hs]) => (
          <div key={tipo}>
            <CabeceraSeccion titulo={TIPOS_HALLAZGO[tipo]} n={hs.length} />
            <div className="lista">
              {hs.map((h) => (
                <div key={h.id} style={{ padding: "12px 18px", borderLeft: "3px solid var(--verified)" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, flex: 1, minWidth: 240, lineHeight: 1.5 }}>{h.contenido}</span>
                    {h.estado_verificacion === "editado" && <Pastilla estado="muted">editado</Pastilla>}
                    {h.requiere_re_verificacion && <Pastilla estado="conflict">re-verificar</Pastilla>}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 6, fontSize: 12.5, color: "var(--muted)", flexWrap: "wrap" }}>
                    <span>{h.visibilidad === "anonimizada" ? "frontline (anonimizado)" : personas.get(h.persona_id) ?? h.persona_id}</span>
                    <span>{sesionNombre.get(h.sesion_id) ?? h.sesion_id}</span>
                    <span className="mono" style={{ color: "var(--faint)" }}>{fmtMs(h.timestamp_ms)}</span>
                  </div>
                  {h.fragmento_transcripcion && (
                    <blockquote style={{ marginTop: 8, padding: "6px 10px", background: "var(--ice)", borderRadius: "var(--r-control)", fontSize: 13, lineHeight: 1.5 }}>{h.fragmento_transcripcion}</blockquote>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
